import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Zap } from "lucide-react";

const vacio = {
  alumno_nombre: "", alumno_apellidos: "", tutor_nombre: "", telefono: "", email: "",
  sede_id: "", clase_id: "", dia: "", hora_inicio: "", hora_fin: "", plan_id: "", forma_pago: "efectivo",
};

/** Formulario de alta rápida (mostrador): alumno + tutor + sede + clase + plan. */
export const AltaExpressForm = ({ sedes = [], clases = [], planes = [], onEnviar, oscuro = false }) => {
  const [form, setForm] = useState(vacio);
  const [enviando, setEnviando] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pideSede = sedes.length > 0;
  const clasesSede = useMemo(
    () => clases.filter((c) => !pideSede || !form.sede_id || c.sede_id === form.sede_id),
    [clases, form.sede_id, pideSede]);
  const planesSede = useMemo(
    () => planes.filter((p) => !pideSede || !form.sede_id || p.sede_id === form.sede_id),
    [planes, form.sede_id, pideSede]);
  const clase = useMemo(() => clases.find((c) => c.id === form.clase_id), [clases, form.clase_id]);
  const listo = form.alumno_nombre && form.tutor_nombre && form.email && form.clase_id && form.dia
    && (!pideSede || form.sede_id);

  const enviar = async () => {
    setEnviando(true);
    try {
      const ok = await onEnviar(form);
      if (ok) setForm(vacio);
    } finally {
      setEnviando(false);
    }
  };

  const label = oscuro ? "text-neutral-300" : "";
  const input = oscuro ? "bg-neutral-900 border-neutral-700 text-white h-12" : "h-11";

  return (
    <div className="grid gap-4" data-testid="alta-express-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="grid gap-1.5">
          <Label className={label}>Nombre del alumno</Label>
          <Input className={input} value={form.alumno_nombre} data-testid="ae-alumno-nombre"
            onChange={(e) => set("alumno_nombre", e.target.value)} />
        </div>
        <div className="grid gap-1.5">
          <Label className={label}>Apellidos del alumno</Label>
          <Input className={input} value={form.alumno_apellidos} data-testid="ae-alumno-apellidos"
            onChange={(e) => set("alumno_apellidos", e.target.value)} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="grid gap-1.5">
          <Label className={label}>Padre / madre / tutor</Label>
          <Input className={input} value={form.tutor_nombre} data-testid="ae-tutor-nombre"
            onChange={(e) => set("tutor_nombre", e.target.value)} />
        </div>
        <div className="grid gap-1.5">
          <Label className={label}>Teléfono</Label>
          <Input className={input} value={form.telefono} data-testid="ae-telefono"
            onChange={(e) => set("telefono", e.target.value)} />
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label className={label}>Email del tutor</Label>
        <Input className={input} type="email" value={form.email} data-testid="ae-email"
          onChange={(e) => set("email", e.target.value)} />
      </div>
      {pideSede && (
        <div className="grid gap-1.5">
          <Label className={label}>Sede</Label>
          <Select value={form.sede_id}
            onValueChange={(v) => setForm((f) => ({ ...f, sede_id: v, clase_id: "", dia: "", hora_inicio: "", hora_fin: "", plan_id: "" }))}>
            <SelectTrigger className={input} data-testid="ae-sede-select">
              <SelectValue placeholder="Elige la sede" />
            </SelectTrigger>
            <SelectContent>
              {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grid gap-1.5">
        <Label className={label}>Clase</Label>
        <Select value={form.clase_id}
          onValueChange={(v) => setForm((f) => ({ ...f, clase_id: v, dia: "", hora_inicio: "", hora_fin: "" }))}>
          <SelectTrigger className={input} data-testid="ae-clase-select">
            <SelectValue placeholder={pideSede && !form.sede_id ? "Elige primero la sede" : "Elige la clase"} />
          </SelectTrigger>
          <SelectContent>
            {clasesSede.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {clase && (
        <div className="grid gap-1.5">
          <Label className={label}>Día y hora</Label>
          <div className="flex flex-wrap gap-2">
            {(clase.horarios || []).map((h) => {
              const puesto = form.dia === h.dia && form.hora_inicio === h.hora_inicio;
              return (
                <button key={`${h.dia}-${h.hora_inicio}`} type="button"
                  data-testid={`ae-horario-${h.dia}-${h.hora_inicio}`}
                  onClick={() => setForm((f) => ({ ...f, dia: h.dia, hora_inicio: h.hora_inicio, hora_fin: h.hora_fin }))}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${
                    puesto ? "bg-primary text-primary-foreground border-primary"
                      : oscuro ? "border-neutral-700 text-neutral-300" : "border-border text-muted-foreground hover:bg-muted"}`}>
                  {h.dia} · {h.hora_inicio}–{h.hora_fin}
                </button>
              );
            })}
            {(clase.horarios || []).length === 0 && (
              <p className="text-xs text-muted-foreground">Esta clase no tiene horarios configurados.</p>
            )}
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="grid gap-1.5">
          <Label className={label}>Plan (opcional)</Label>
          <Select value={form.plan_id} onValueChange={(v) => set("plan_id", v)}>
            <SelectTrigger className={input} data-testid="ae-plan-select">
              <SelectValue placeholder="Sin plan por ahora" />
            </SelectTrigger>
            <SelectContent>
              {planesSede.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.nombre} · {p.importe} €</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label className={label}>Cómo paga</Label>
          <Select value={form.forma_pago} onValueChange={(v) => set("forma_pago", v)}>
            <SelectTrigger className={input} data-testid="ae-formapago-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="efectivo">Efectivo (genera cobro pendiente)</SelectItem>
              <SelectItem value="transferencia">Transferencia (genera cobro pendiente)</SelectItem>
              <SelectItem value="stripe">Online desde el portal</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className={`text-xs ${oscuro ? "text-neutral-400" : "text-muted-foreground"}`}>
        Al guardar, el tutor recibe un email para completar su perfil, crear su contraseña y dar el
        consentimiento RGPD y la autorización de uso de imagen. Hasta entonces la ficha queda
        marcada como pendiente de consentimiento.
      </p>
      <Button className="rounded-full font-semibold h-12" onClick={enviar} disabled={enviando || !listo}
        data-testid="ae-guardar-btn">
        {enviando ? <Loader2 className="h-4 w-4 animate-spin" />
          : <><Zap className="h-4 w-4 mr-2" /> Dar de alta y enviar consentimiento</>}
      </Button>
    </div>
  );
};
